import { useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, HelpCircle, Sparkles, Users, Utensils } from "lucide-react";
import { Link } from "wouter";
import { Chip } from "@/components/ux/Chip";
import { Expandable } from "@/components/ux/Expandable";
import { SectionBackground } from "@/components/ux/SectionBackground";
import Logo from "@/components/Logo";
import { useT } from "@/lib/i18n";
import tracking from "../../lib/tracking";
import sapphicPeopleImg from "../../assets/images/lesbian people2.webp";
import timeoutImg from "../../assets/images/timeout.svg";
import themImg from "../../assets/images/them.svg";

/**
 * Problem-first sapphic 20-39 test page (vs /t8nw minimal).
 *
 * Structure follows the CRO test spec (2026-08-15):
 * hero = the reader's problem, not the product; copy in audience language;
 * exactly one CTA (quiz); testimonials placed directly after the biggest
 * objection ("is this a dating thing?"); H1->H2 sequence tells the whole
 * story without body text; sticky mobile CTA (91.6% of traffic is mobile).
 */

const POSTHOG_EVENT = "sapphic_story_landing";

function quizHref(): string {
  const { hostname } = window.location;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
  const base = isLocal ? "/app/quiz" : "https://app.outclose.com/quiz";
  return `${base}?a=s`;
}

const FEATURES = [
  { icon: Sparkles, key: "quiz" },
  { icon: Users, key: "matched" },
  { icon: Utensils, key: "booked" },
] as const;

const TESTIMONIAL_KEYS = ["jess", "sam", "maya"] as const;
const FAQ_KEYS = ["dating", "shy", "who", "cost", "click"] as const;

function CtaButton({ label, sub }: { label: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center">
      <a
        href={quizHref()}
        className="rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-lg px-10 py-4 shadow-lg shadow-primary-500/40 w-full sm:w-auto inline-flex items-center justify-center transition-colors"
      >
        {label}
        <Sparkles className="inline-block ml-2 w-5 h-5" />
      </a>
      {sub && (
        <p className="text-sm text-dark-muted dark:text-neutral-400 mt-2">{sub}</p>
      )}
    </div>
  );
}

export default function LandingSapphicStory() {
  const { t } = useT("landing");

  useEffect(() => {
    tracking.trackPage(POSTHOG_EVENT);
  }, []);

  const k = (key: string) => t(`pages.sapphicStory.${key}`);

  return (
    <div className="relative overflow-x-hidden pb-24 md:pb-0">
      <div className="max-w-7xl mx-auto px-6 flex items-center my-4 justify-between">
        <Logo withIcon={true} />
      </div>

      {/* 1 — Hero: the problem, before the product */}
      <div className="max-w-3xl mx-auto px-6 pt-8 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center"
        >
          <h1 className="text-4xl md:text-6xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins leading-tight mb-6">
            {k("hero.h1")}
          </h1>
          <p className="text-lg md:text-xl text-dark-muted dark:text-neutral-300 mb-8 max-w-xl">
            {k("hero.sub")}
          </p>
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl shadow-lg mb-8">
            <img
              src={sapphicPeopleImg}
              alt={k("hero.imageAlt")}
              className="w-full object-cover"
            />
          </div>
          <CtaButton label={k("cta")} sub={k("ctaSub")} />
        </motion.div>
      </div>

      {/* 2 — The solution, scannable */}
      <SectionBackground className="px-6 py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins mb-3 max-w-2xl mx-auto">
          {k("solution.h2")}
        </h2>
        <p className="text-dark-muted dark:text-neutral-300 mb-10 max-w-xl mx-auto">
          {k("solution.sub")}
        </p>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto text-left">
          {FEATURES.map(({ icon: Icon, key }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * (i + 1), duration: 0.5 }}
              className="card p-6"
            >
              <Icon className="w-6 h-6 text-primary-500 mb-3" />
              <h3 className="font-semibold text-type-950 dark:text-neutral-100 mb-2">
                {k(`features.${key}.title`)}
              </h3>
              <p className="text-dark-muted dark:text-neutral-400 text-sm">
                {k(`features.${key}.body`)}
              </p>
            </motion.div>
          ))}
        </div>
      </SectionBackground>

      {/* 3 — The objection, answered head-on… */}
      <div className="px-6 py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins mb-3 max-w-2xl mx-auto">
          {k("objection.h2")}
        </h2>
        <p className="text-dark-muted dark:text-neutral-300 max-w-xl mx-auto mb-10">
          {k("objection.body")}
        </p>

        {/* …with the receipts directly underneath */}
        <Chip
          title={k("testimonials.chipTitle")}
          variant="primary"
          icon={<MessageCircle strokeWidth={2} />}
        />
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-8">
          {TESTIMONIAL_KEYS.map((key, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * (i + 1), duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                {k(`testimonials.${key}.quote`)}
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {k(`testimonials.${key}.author`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 4 — Press */}
      <div className="px-6 pb-16 text-center">
        <p className="text-xs uppercase tracking-widest text-dark-muted dark:text-neutral-500 mb-4">
          {k("press.label")}
        </p>
        <div className="flex items-center justify-center gap-10">
          <a href="https://www.timeout.com/newyork/news/this-new-site-sets-lgbtq-new-yorkers-up-on-friend-datesso-i-sat-down-for-dinner-with-strangers-111225" target="_blank" rel="noreferrer">
            <img src={timeoutImg} alt="Time Out" className="h-10 dark:invert" />
          </a>
          <a href="https://www.them.us/story/outclose-gayborhood-app-queer-dinners-finding-friends" target="_blank" rel="noreferrer">
            <img src={themImg} alt="Them" className="h-8 dark:invert" />
          </a>
        </div>
      </div>

      {/* 5 — Close: result + timeframe, minus the feared thing */}
      <SectionBackground className="px-6 py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins mb-8 max-w-2xl mx-auto">
          {k("close.h2")}
        </h2>
        <CtaButton label={k("cta")} sub={k("ctaSub")} />
        <div className="max-w-md mx-auto mt-10 card p-6 text-left">
          <p className="text-dark-muted dark:text-neutral-400 italic mb-3">
            {k("close.reassureQuote")}
          </p>
          <p className="font-semibold text-type-950 dark:text-neutral-100">
            {k("close.reassureAuthor")}
          </p>
        </div>
      </SectionBackground>

      {/* 6 — FAQ */}
      <div className="px-6 py-16 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Chip
            title={k("faq.chipTitle")}
            variant="primary"
            icon={<HelpCircle strokeWidth={2} />}
          />
        </div>
        <div className="flex flex-col gap-3">
          {FAQ_KEYS.map((key) => (
            <Expandable key={key} title={k(`faq.${key}.q`)}>
              {k(`faq.${key}.a`)}
            </Expandable>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 pb-10 text-center text-xs text-dark-muted dark:text-neutral-500">
        <p className="flex items-center justify-center gap-1.5 mb-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
          {t("footer.proudlyLgbtqOwned")}
        </p>
        <p>
          <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
            {t("footer.terms")}
          </Link>
          {" · "}
          <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
            {t("footer.privacy")}
          </Link>
        </p>
      </div>

      {/* Sticky mobile CTA — same single goal, always reachable */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/90 dark:bg-neutral-900/90 backdrop-blur border-t border-neutral-200 dark:border-neutral-800 p-3 z-50">
        <a
          href={quizHref()}
          className="rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-base py-3 w-full inline-flex items-center justify-center transition-colors"
        >
          {k("cta")}
        </a>
      </div>
    </div>
  );
}
